import { Box, Typography, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiHome, FiMail, FiPhone, FiUser } from "react-icons/fi";
import TextBox from "../components/input/TextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Building } from "../db_access/building";
import { Product } from "../db_access/product";
import { Project } from "../db_access/project";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import { useTheme } from "@mui/material";

const SupplierInfo = ({ selectedRow, type = "project", setBuildingData, refreshBuildingData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { isSaveClicked, setIsSaveVisible, setIsSaveClicked, setIsLoading, setRefresh } = useContextProvider();
  const [modified, setModified] = useState(false);
  const [formData, setFormData] = useState({
    detail_address: "",
    detail_contact_person: "",
    detail_email: "",
    detail_phone: "",
    isEpd: false,
  });

  useEffect(() => {
    if (selectedRow) {
      const isEpdValue = selectedRow.is_epd === 1;
      setFormData({
        id: selectedRow.id || "",
        detail_contact_person: selectedRow.detail_contact_person || "",
        detail_email: selectedRow.detail_email || "",
        detail_phone: selectedRow.detail_phone || "",
        detail_address: selectedRow.detail_address || "",
        isEpd: isEpdValue,
      });
      setModified(false);
    }
  }, [selectedRow]);

  const handleChange = (field) => (e) => {
    if (formData.isEpd) return;
    const value = field === "isEpd" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    setIsSaveVisible(true);
    setModified(true);

    if (type === "building" && setBuildingData) {
      setBuildingData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  useEffect(() => {
    if (isSaveClicked && modified) {
      const fd = new FormData();
      Object.keys(formData).forEach((k) => {
        fd.append(k, formData[k]);
      });

      //  Choose API based on type prop
      let request;
      switch (type) {
        case "building":
          request = Building.save_supplier_info;
          break;
        case Product.label:
          request = Product.save_supplier_info;
          break;
        case Project.label:
          request = Project.save_supplier_info;
          break;
        default:
          break;
      }

      request(fd)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to update");
          return res.json();
        })
        .then((updated) => {
          // Update local building data after successful save
          if (type === "building" && setBuildingData) {
            setBuildingData((prev) => ({
              ...prev,
              detail_contact_person: formData.detail_contact_person,
              detail_email: formData.detail_email,
              detail_phone: formData.detail_phone,
              detail_address: formData.detail_address,
            }));
          }
          if (type === "building" && refreshBuildingData) {
            refreshBuildingData();
          }
        })
        .catch((err) => {
          console.error("Error:", err);
        })
        .finally(() => {
          setModified(false);
          setIsSaveClicked(false);
          setIsLoading(false);
          setRefresh((r) => !r);
        });
    }
  }, [isSaveClicked, modified, formData, selectedRow, type, setIsSaveClicked, setIsLoading, setRefresh]);

  const formContentLeft = [
    { label: "Contact Person", field: "detail_contact_person", icon: FiUser },
    { label: "Email", field: "detail_email", icon: FiMail },
  ];

  const formContentRight = [
    { label: "Phone number", field: "detail_phone", icon: FiPhone },
    { label: "Address", field: "detail_address", icon: FiHome },
  ];

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Supplier Info")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", gap: 3 }} style={{ flex: 1 }}>
            {formContentLeft.map((f, i) => (
              <Box key={i} style={{ flex: 1 }}>
                <TextBox placeholder={f.label} value={formData[f.field]} onChange={handleChange(f.field)} disabled={formData.isEpd} label={f.label} Icon={f.icon} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 3 }} style={{ flex: 1 }}>
            {formContentRight.map((f, i) => (
              <Box key={i} style={{ flex: 1 }}>
                <TextBox placeholder={f.label} value={formData[f.field]} onChange={handleChange(f.field)} disabled={formData.isEpd} label={f.label} Icon={f.icon} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SupplierInfo;
